import { useState, useEffect } from 'react';
import { Loader2, Save, Plus, X, Settings2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';

interface Config {
  is_paused: number;
  warmup_week: number;
  daily_limit_override: number | null;
  send_hour_start: number;
  send_hour_end: number;
  from_email: string;
  reply_to: string;
  followup1_delay_days: number;
  followup2_delay_days: number;
  custom_holidays: string;
}

export default function OutreachSettingsPage() {
  const [config, setConfig] = useState<Config | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [holidays, setHolidays] = useState<string[]>([]);
  const [newHoliday, setNewHoliday] = useState('');

  useEffect(() => {
    fetch('/api/outreach/config')
      .then(r => r.json())
      .then((d: unknown) => {
        const data = d as { config: Config };
        setConfig(data.config);
        try {
          setHolidays(JSON.parse(data.config.custom_holidays) as string[]);
        } catch {
          setHolidays([]);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (!config) return;
    setSaving(true);
    try {
      await fetch('/api/outreach/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...config,
          custom_holidays: JSON.stringify(holidays),
        }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  const addHoliday = () => {
    if (newHoliday && /^\d{4}-\d{2}-\d{2}$/.test(newHoliday) && !holidays.includes(newHoliday)) {
      setHolidays(h => [...h, newHoliday].sort());
      setNewHoliday('');
    }
  };

  if (loading || !config) {
    return <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-teal-500" /></div>;
  }

  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader
        type="outreach"
        icon={Settings2}
        title="Ajustes Outreach"
      />

      <div className="space-y-5">
        {/* Campaign control */}
        <section className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Control de campaña</h3>

          <label className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-700">Campaña pausada</p>
              <p className="text-sm text-gray-400">Detiene todos los envíos automáticos</p>
            </div>
            <button
              onClick={() => setConfig(c => c ? { ...c, is_paused: c.is_paused ? 0 : 1 } : c)}
              className={`relative w-12 h-6 rounded-full transition-colors ${config.is_paused ? 'bg-red-500' : 'bg-gray-200'}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${config.is_paused ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </label>
        </section>

        {/* Warmup */}
        <section className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Calentamiento</h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Semana de calentamiento (1–8)
              </label>
              <input
                type="number"
                min={1} max={8}
                value={config.warmup_week}
                onChange={e => setConfig(c => c ? { ...c, warmup_week: parseInt(e.target.value) || 1 } : c)}
                className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <p className="text-xs text-gray-400 mt-1">Wk1=5, Wk2=10, Wk3=20, Wk4=30, Wk5+=50 emails/día</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Límite diario manual (vacío = automático por semana)
              </label>
              <input
                type="number"
                min={0}
                value={config.daily_limit_override ?? ''}
                onChange={e => setConfig(c => c ? { ...c, daily_limit_override: e.target.value ? parseInt(e.target.value) : null } : c)}
                placeholder="Auto"
                className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>
        </section>

        {/* Sending window */}
        <section className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Ventana de envío (CET)</h3>
          <div className="flex gap-4 items-center">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Desde (hora)</label>
              <input
                type="number" min={7} max={23}
                value={config.send_hour_start}
                onChange={e => setConfig(c => c ? { ...c, send_hour_start: parseInt(e.target.value) } : c)}
                className="w-20 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <span className="text-gray-400 mt-5">—</span>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Hasta (hora)</label>
              <input
                type="number" min={7} max={23}
                value={config.send_hour_end}
                onChange={e => setConfig(c => c ? { ...c, send_hour_end: parseInt(e.target.value) } : c)}
                className="w-20 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>
        </section>

        {/* Email config */}
        <section className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Email</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From email</label>
              <input
                type="email"
                value={config.from_email}
                onChange={e => setConfig(c => c ? { ...c, from_email: e.target.value } : c)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reply-to</label>
              <input
                type="email"
                value={config.reply_to}
                onChange={e => setConfig(c => c ? { ...c, reply_to: e.target.value } : c)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>
        </section>

        {/* Follow-up delays */}
        <section className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Cadencia de seguimiento</h3>
          <div className="flex gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Paso 1 → Paso 2 (días)</label>
              <input
                type="number" min={1} max={30}
                value={config.followup1_delay_days}
                onChange={e => setConfig(c => c ? { ...c, followup1_delay_days: parseInt(e.target.value) || 3 } : c)}
                className="w-20 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Paso 2 → Paso 3 (días)</label>
              <input
                type="number" min={1} max={30}
                value={config.followup2_delay_days}
                onChange={e => setConfig(c => c ? { ...c, followup2_delay_days: parseInt(e.target.value) || 7 } : c)}
                className="w-20 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-2">Recomendado: 3 y 7 días (basado en Woodpecker data)</p>
        </section>

        {/* Custom holidays */}
        <section className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-1">Días festivos extra</h3>
          <p className="text-sm text-gray-400 mb-3">
            Los festivos nacionales españoles + Canarias ya están incluidos por defecto.
            Añade aquí fechas adicionales en formato YYYY-MM-DD.
          </p>
          <div className="flex gap-2 mb-3">
            <input
              type="date"
              value={newHoliday}
              onChange={e => setNewHoliday(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            <button onClick={addHoliday} className="btn-secondary flex items-center gap-1.5 px-3 py-2 text-sm">
              <Plus size={14} /> Añadir
            </button>
          </div>
          {holidays.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {holidays.map(h => (
                <span key={h} className="flex items-center gap-1 bg-gray-100 text-gray-700 px-2.5 py-1 rounded-full text-sm">
                  {h}
                  <button onClick={() => setHolidays(hs => hs.filter(x => x !== h))}
                    className="text-gray-400 hover:text-red-500">
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </section>

        {/* Save button */}
        <button
          onClick={save}
          disabled={saving}
          className="w-full btn-primary py-3 flex items-center justify-center gap-2 text-base"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {saved ? '¡Guardado!' : 'Guardar ajustes'}
        </button>
      </div>
    </div>
  );
}
